/// <amd-module name="Build/Common/Artifacts/providers/Base" />
import * as React from "react";

import * as Resources from "Build/Common/Artifacts/Resources";

import { BuildArtifact } from 'Build/Client/Contracts/Build';
import { IBuildRestClient } from "Build/Client/RestClient/Build";

import { openLinkSecurely } from "Build/Common/Library/Navigation";

import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IArtifactProvider, IDisplayOptions, BuildArtifactTypes, ArtifactProviderContext } from 'Common';
import { format } from 'VSS/Core/Util/String';

export abstract class Base implements IArtifactProvider {
    protected pageContext: IVssPageContext;
    protected artifact: BuildArtifact;
    protected providerContext: ArtifactProviderContext;

    public abstract getKey(): string;

    constructor(pageContext: IVssPageContext, artifact: BuildArtifact, providerContext: ArtifactProviderContext) {
        this.pageContext = pageContext;
        this.artifact = artifact;
        this.providerContext = providerContext;
    }

    public getArtifact() {
        return this.artifact;
    }

    public getProviderContext() {
        return this.providerContext;
    }

    public supportsDownload(): boolean {
        return false;
    }

    public supportsExploring(): boolean {
        return false;
    }

    public getDisplayOptions(): IDisplayOptions {
        return {
            text: this.artifact.name,
            iconName: "ZipFolder",
            tooltip: format(Resources.BuildArtifactFormat, this.artifact.name),
            type: this.getType()
        }
    }

    public onDownload() {
        if (this.supportsDownload()) {
            this.openLink(this.getDownloadURL());
        }
    }

    public onExplore() {
        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss) => {
            const props = {
                modules: ["Build/Scripts/ArtifactExplorerDialogLWP"],
                wrappedType: "ci-artifacts-explorer-dialog",
                artifact: this.artifact,
                buildId: this.providerContext.buildId,
                projectId: this.providerContext.projectId
            };
            return React.createElement(LegacyComponent, props);
        });
    }

    public getDownloadURL() {
        if (this.supportsDownload()) {
            return this.artifact.resource.downloadUrl;
        }

        return "";
    }

    protected showCopyDialog() {
        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss) => {
            const props = {
                modules: ["Build/Scripts/ArtifactExplorerDialogLWP"],
                wrappedType: "ci-artifacts-copy-dialog",
                content: this.artifact.resource.downloadUrl
            };
            return React.createElement(LegacyComponent, props);
        });
    }

    protected getType(): string {
        const key = this.getKey();
        switch (key) {
            case BuildArtifactTypes.Container:
                return Resources.ContainerType;
            case BuildArtifactTypes.PipelineArtifact:
                return Resources.PipelineArtifact;
            case BuildArtifactTypes.FilePath:
                return Resources.FilePathType;
            case BuildArtifactTypes.GitRef:
                return Resources.GitRefType;
            case BuildArtifactTypes.SymbolRequest:
                return Resources.SymbolRequestType;
            case BuildArtifactTypes.SymbolStore:
                return Resources.SymbolStoreType;
            case BuildArtifactTypes.TfvcLabel:
                return Resources.TfvcLabelType;
            case BuildArtifactTypes.VersionControl:
                return Resources.VersionControlType;
        }

        return Resources.UnknownType;
    }

    protected openLink(link: string) {
        openLinkSecurely(link);
    }
}
